import type { ColumnFiltersState, FilterFn, Row } from '@tanstack/react-table';

/**
 * Default filter function for DataGrid columns. Handles three filter value types:
 *
 * 1. **Boolean** — Toggle filters. Currently unreachable via the standard DataGrid flow because
 *    `updateColumnFilters` wraps all values in arrays (line 49) and removes `false` toggles
 *    entirely (line 41). Kept as a safety net for direct column filter manipulation.
 *
 * 2. **Array** — The primary path for both select and toggle filters. `updateColumnFilters`
 *    normalizes all values to arrays, so toggle `true` arrives as `[true]` and select values
 *    arrive as `[string, ...]`. Performs case-insensitive matching for strings, strict
 *    equality for other types (including booleans).
 *
 * 3. **String** — Text search filters. Case-insensitive substring match against the row value.
 */
export const defaultFilterFn: FilterFn<unknown> = (row: Row<unknown>, columnId: string, filterValue: unknown): boolean => {
  const rowValue = row.getValue<unknown>(columnId);

  // Handle boolean filters (toggle filters)
  // Note: Currently unreachable — `updateColumnFilters` wraps booleans in arrays before they
  // reach TanStack, so toggle filters hit the array branch below instead. This branch exists
  // as a fallback for any code that sets column filters directly without `updateColumnFilters`.
  if (typeof filterValue === 'boolean') {
    return Boolean(rowValue) === filterValue;
  }

  // Handle array filters (select and toggle filters)
  // This is the primary filter path — `updateColumnFilters` normalizes all values to arrays.
  // Toggle filters arrive as [true], select filters as [string, ...].
  if (Array.isArray(filterValue)) {
    if (filterValue.length === 0) return true; // No filter applied
    return filterValue.some((value) => {
      if (typeof value === 'string' && typeof rowValue === 'string') {
        return rowValue.toLowerCase() === value.toLowerCase();
      }
      return rowValue === value;
    });
  }

  // Handle string filters (text search)
  if (typeof filterValue === 'string') {
    if (typeof rowValue !== 'string') return false;
    return rowValue.toLowerCase().includes(filterValue.toLowerCase());
  }

  return true; // Unrecognized filter type — don't exclude the row
};

/**
 * Immutably updates a column filter in the filter state array. Acts as the single entry point
 * for all filter changes in DataGrid — called by `DataGridFilters` toggle, select, and date
 * filter components.
 *
 * **Key behavior:** All filter values are normalized to arrays before storage (line 68) so that
 * `defaultFilterFn` receives a consistent shape. This means:
 * - Toggle `true` is stored as `[true]`
 * - Select `['Admin', 'User']` is stored as-is
 * - A single select value `'Admin'` is stored as `['Admin']`
 *
 * **Removal logic:** A filter is removed (not stored) when its value is "empty":
 * - Empty arrays `[]` — no select options chosen
 * - Boolean `false` — toggle turned off
 * - `undefined` or `null` — explicitly cleared
 *
 * Note: Empty strings are not treated as "no filter" — an empty string `''` will be stored as
 * `['']` and will match only rows where the cell value is literally an empty string.
 */
export const updateColumnFilters = (currentFilters: ColumnFiltersState, filterId: string, value: unknown): ColumnFiltersState => {
  const otherFilters = currentFilters.filter((f) => f.id !== filterId);

  // Remove filter when select has no options chosen
  if (Array.isArray(value) && value.length === 0) {
    return otherFilters;
  }

  // Remove filter when toggle is turned off
  if (typeof value === 'boolean' && !value) {
    return otherFilters;
  }

  // Add or replace the filter, normalizing all values to arrays for consistency
  if (value !== undefined && value !== null) {
    const arrayValue = Array.isArray(value) ? value : [value];
    return [...otherFilters, { id: filterId, value: arrayValue }];
  }

  return otherFilters;
};
